/* ═══════════════════════════════════════════════════════════════
   OrgService — Organization CRUD via EC2 API (cloud, in-memory cache)
   ═══════════════════════════════════════════════════════════════ */

import type { Organization } from '../../shared/types';
import * as cache from './cloudCache';
import type { TableOpts } from './cloudCache';
import * as cloudApi from './apiClient';
import { getCurrentFirmId } from './firmContext';
import { log as logActivity } from './activityLogger';

const orgOpts: TableOpts = {
  jsonFields: ['addresses', 'contacts'],
};

export const OrgService = {
  list: async (): Promise<Organization[]> => cache.list<Organization>('organizations', orgOpts),

  save: async (org: Organization): Promise<Organization> =>
    cache.save('organizations', org, orgOpts),

  delete: async (id: string): Promise<void> => cache.remove('organizations', id),

  /** Archive or restore — a THREE-key partial update on purpose: the server
   *  reads an `archived_at` flip of three keys or fewer as an archive toggle
   *  and gates it on `organizations.archive`. Mirrors ModuleService.setArchived. */
  setArchived: async (id: string, archived: boolean, userName?: string): Promise<void> => {
    const firmId = getCurrentFirmId();
    await cloudApi.dbUpdate(
      'organizations',
      {
        archived_at: archived ? new Date().toISOString() : null,
        archived_by: archived ? userName || null : null,
      },
      [
        { col: 'firm_id', op: '=', val: firmId },
        { col: 'id', op: '=', val: id },
      ]
    );
    cache.invalidate('organizations');
    logActivity({
      action: archived ? 'organization.archive' : 'organization.restore',
      entityType: 'organization',
      entityId: id,
    });
  },
};
